import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { CSVLink } from "react-csv";
import API from "../../services/api";
import { BrandsTypes } from "../../utils/constants";
import TableLoader from "../../shared/TableLoader";
import NoDataFound from "../../shared/NoDataFound";
import MultiSelectFilter from "../../shared/MultiSelectFilter";
import { formatSpentValue, getDayKey, formatDisplayDate } from "../../utils/constants";

const formatRegionLabel = (region = "") =>
  String(region)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());


function RegionWiseReport({ dateRange, initiateDownloadCSV, setInitiateDownloadCSV, selectedSalesPersonId }) {
  const [regionData, setRegionData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const csvLinkRef = useRef(null);
  const [selectedColumnKeys, setSelectedColumnKeys] = useState([]);

  const fetchData = async () => {
    if (!dateRange?.[0] || !dateRange?.[1]) {
      setRegionData([]);
      return;
    }
    try {
      setLoader(true);
      const response = await API({
        url: "/alpha/get-region-wise-reports",
        method: "POST",
        responseType: "json",
        data: { dateRange, sales_person_id: +selectedSalesPersonId || undefined },
      });
      if (response?.code === 200) {
        setRegionData(Array.isArray(response.data) ? response.data : []);
      } else {
        setRegionData([]);
      }
    } catch (error) {
      console.error("Error fetching region data", error);
      setRegionData([]);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange, selectedSalesPersonId]);

  const groupedHeaders = useMemo(() => {
    const filtered = (regionData || [])

    const uniqueMap = new Map();
    filtered.forEach((record) => {
      const brand = BrandsTypes.find((i) => i.value === record.new_user_type);
      if (!brand) return;
      const key = `${record.new_user_type}-${record.region}`;
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, {
          key,
          label: `${brand.label}-${formatRegionLabel(record.region)}`,
          new_user_type: record.new_user_type,
          region: record.region,
        });
      }
    });

    let sortedHeaders = Array.from(uniqueMap.values()).sort((a, b) => {
      const brandIndexA = BrandsTypes.findIndex((item) => item.value === a.new_user_type);
      const brandIndexB = BrandsTypes.findIndex((item) => item.value === b.new_user_type);
      if (brandIndexA !== brandIndexB) return brandIndexA - brandIndexB;
      return String(a.region).localeCompare(String(b.region));
    });
    // Sort the headers
    let toBeLastColsKeyWords = ['classified', 'inbound']
    let lastCols = sortedHeaders.filter((col) => toBeLastColsKeyWords.some((keyword) => col.label.toLowerCase().includes(keyword)));
    let firstCols = sortedHeaders.filter((col) => !lastCols.includes(col));
    return [...firstCols, ...lastCols];
  }, [regionData]);

  const visibleHeaders = useMemo(
    () => (selectedColumnKeys.length ? groupedHeaders.filter((h) => selectedColumnKeys.includes(h.key)) : groupedHeaders),
    [groupedHeaders, selectedColumnKeys]
  );

  const rows = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return [];

    const filtered = (regionData || [])

    const recordsByDay = new Map();
    filtered.forEach((record) => {
      const key = getDayKey(new Date(record.date * 1000));
      if (!recordsByDay.has(key)) {
        recordsByDay.set(key, []);
      }
      recordsByDay.get(key).push(record);
    });

    const start = new Date(dateRange[0]);
    const end = new Date(dateRange[1]);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const result = [];
    for (let current = new Date(start); current <= end; current.setDate(current.getDate() + 1)) {
      const currentDate = new Date(current);
      const dayKey = getDayKey(currentDate);
      const dayRecords = recordsByDay.get(dayKey) || [];

      const metrics = {};

      groupedHeaders.forEach((header) => {
        const matched = dayRecords.filter(
          (record) =>
            record.new_user_type === header.new_user_type &&
            record.region === header.region
        );

        const ads = matched.reduce((sum, record) => sum + Number(record.totalAds || 0), 0);
        const revenue = matched.reduce((sum, record) => sum + Number(record.totalSpent || 0), 0);

        metrics[header.key] = { ads, revenue };
      });

      // Total reflects only the currently visible/filtered headers.
      const totalAds = visibleHeaders.reduce((sum, header) => sum + (metrics[header.key]?.ads || 0), 0);
      const totalRevenue = visibleHeaders.reduce((sum, header) => sum + (metrics[header.key]?.revenue || 0), 0);

      result.push({
        date: currentDate,
        metrics,
        totalAds,
        totalRevenue,
      });
    }

    return result;
  }, [dateRange, groupedHeaders, visibleHeaders, regionData]);

  const totals = useMemo(() => {
    const overall = {
      totalAds: 0,
      totalRevenue: 0,
      byHeader: {},
    };

    groupedHeaders.forEach((header) => {
      overall.byHeader[header.key] = { ads: 0, revenue: 0 };
    });

    rows.forEach((row) => {
      overall.totalAds += row.totalAds;
      overall.totalRevenue += row.totalRevenue;
      groupedHeaders.forEach((header) => {
        overall.byHeader[header.key].ads += row.metrics[header.key]?.ads || 0;
        overall.byHeader[header.key].revenue += row.metrics[header.key]?.revenue || 0;
      });
    });

    return overall;
  }, [groupedHeaders, rows]);

  const colspan = visibleHeaders.length * 2 + 3;

  const handleDownloadCSV = useCallback(() => {
    // Totals here are computed across the full groupedHeaders (not visibleHeaders) so
    // the export always reflects every column, independent of the on-screen filter.
    if (!groupedHeaders.length || !rows.length) return;
    const csvRows = rows.map((row) => {
      const obj = { Date: formatDisplayDate(row.date) };
      let rowTotalAds = 0;
      let rowTotalRevenue = 0;
      groupedHeaders.forEach((header) => {
        const ads = row.metrics[header.key]?.ads || 0;
        const revenue = row.metrics[header.key]?.revenue || 0;
        obj[`${header.label} - No. of Ads`] = ads ?? "";
        obj[`${header.label} - Revenue`] = revenue != null ? revenue : "";
        rowTotalAds += ads;
        rowTotalRevenue += revenue;
      });
      obj["Total - No. of Ads"] = rowTotalAds;
      obj["Total - Revenue"] = rowTotalRevenue;
      return obj;
    });
    const grandTotalAds = groupedHeaders.reduce((sum, h) => sum + (totals.byHeader[h.key]?.ads || 0), 0);
    const grandTotalRevenue = groupedHeaders.reduce((sum, h) => sum + (totals.byHeader[h.key]?.revenue || 0), 0);
    const totalRow = {
      Date: "Grand Total",
      ...Object.fromEntries(
        groupedHeaders.flatMap((header) => [
          [`${header.label} - No. of Ads`, totals.byHeader[header.key]?.ads ?? ""],
          [
            `${header.label} - Revenue`,
            totals.byHeader[header.key]?.revenue != null
              ? totals.byHeader[header.key].revenue
              : "",
          ],
        ])
      ),
      "Total - No. of Ads": grandTotalAds,
      "Total - Revenue": grandTotalRevenue,
    };
    setCsvData([...csvRows, totalRow]);
  }, [groupedHeaders, rows, totals]);

  useEffect(() => {
    if (csvData.length > 0) {
      csvLinkRef.current?.link?.click();
      setCsvData([]);
    }
  }, [csvData]);

  useEffect(() => {
    if (!initiateDownloadCSV) return;
    handleDownloadCSV();
    setInitiateDownloadCSV(false);
  }, [initiateDownloadCSV, handleDownloadCSV, setInitiateDownloadCSV]);

  const csvFilename = `region_wise_report_${dateRange?.[0] ? new Date(dateRange[0]).toISOString().slice(0, 10) : "start"}_${dateRange?.[1] ? new Date(dateRange[1]).toISOString().slice(0, 10) : "end"}.csv`;

  return (
    <div className="alpha-report alpha-report-region-wise">
        <CSVLink
          data={csvData}
          filename={csvFilename}
          className="d-none"
          ref={csvLinkRef}
        />
      <MultiSelectFilter
        label="Columns"
        options={groupedHeaders.map((h) => ({ value: h.key, label: h.label }))}
        selected={selectedColumnKeys}
        onChange={setSelectedColumnKeys}
      />
      <div className="table-responsive alpha-report-table-wrap">
        <table className="table table-bordered table-sm align-middle alpha-report-table">
          <colgroup>
            <col style={{ width: "var(--sticky-width)" }} />
            {visibleHeaders.flatMap((h) => [
              <col key={`${h.key}-ads`} style={{ width: 70 }} />,
              <col key={`${h.key}-rev`} style={{ width: 70 }} />,
            ])}
            <col key="total-ads" style={{ width: 70 }} />
            <col key="total-rev" style={{ width: 70 }} />
          </colgroup>
          <thead>
            <tr>
              <th rowSpan={2} className="alpha-report-th-sticky-first">
                Date
              </th>
              {visibleHeaders.map((header) => (
                <th key={header.key} colSpan={2} className="text-center alpha-report-th-group" style={{ minWidth: 140 }}>
                  {header.label}
                </th>
              ))}
              <th colSpan={2} className="text-center alpha-report-th-group" style={{ minWidth: 140 }}>
                Total
              </th>
            </tr>
            <tr>
              {visibleHeaders.map((header) => (
                <React.Fragment key={`${header.key}-sub`}>
                  <th className="alpha-report-th-sub" style={{ minWidth: 70 }}>No. of Ads</th>
                  <th className="alpha-report-th-sub" style={{ minWidth: 70 }}>Revenue</th>
                </React.Fragment>
              ))}
              <th className="alpha-report-th-sub">No. of Ads</th>
              <th className="alpha-report-th-sub">Revenue</th>
            </tr>
          </thead>
          {loader ? (
            <TableLoader colspan={colspan} />
          ) : !visibleHeaders.length ? (
            <NoDataFound colspan={colspan} />
          ) : (
            <tbody>
              {rows.map((row) => (
                <tr key={getDayKey(row.date)}>
                  <td
                    title={formatDisplayDate(row.date)}
                    className="alpha-report-td-sticky-first alpha-report-td-sticky-first-truncate"
                  >
                    {formatDisplayDate(row.date)}
                  </td>
                  {visibleHeaders.map((header) => (
                    <React.Fragment key={`${header.key}-${getDayKey(row.date)}`}>
                      <td>{row.metrics[header.key]?.ads || 0}</td>
                      <td title={row.metrics[header.key]?.revenue}>{row.metrics[header.key]?.revenue ? formatSpentValue(row.metrics[header.key]?.revenue) : ""}</td>
                    </React.Fragment>
                  ))}
                  <td>{row.totalAds}</td>
                  <td title={row.totalRevenue}>{row.totalRevenue ? formatSpentValue(row.totalRevenue) : ""}</td>
                </tr>
              ))}
              <tr className="fw-bold">
                <td className="alpha-report-td-grand-total-sticky">Grand Total</td>
                {visibleHeaders.map((header) => (
                  <React.Fragment key={`${header.key}-total`}>
                    <td className="alpha-report-td-grand-total">{totals.byHeader[header.key]?.ads || 0}</td>
                    <td title={totals.byHeader[header.key]?.revenue} className="alpha-report-td-grand-total">
                      {totals.byHeader[header.key]?.revenue ? formatSpentValue(totals.byHeader[header.key]?.revenue) : ""}
                    </td>
                  </React.Fragment>
                ))}
                <td className="alpha-report-td-grand-total">{totals.totalAds}</td>
                <td title={totals.totalRevenue} className="alpha-report-td-grand-total">
                  {totals.totalRevenue ? formatSpentValue(totals.totalRevenue) : ""}
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>
    </div>
  );
}

export default RegionWiseReport